/**
 * Validation utilities for guest form
 */

import { isValidEmail, isValidPhone } from '../../../utils/checkoutValidation';

export interface GuestFormValues {
  fullName: string;
  gender: string;
  email: string;
  phone: string;
  countryOfIssuance: string;
  passportNumber: string;
  expiryDate: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  certificateLevel: string;
  certifyingAgency: string;
  certificateNumber: string;
  airportTransfer: boolean;
  arrivalFlight: string;
  departureFlight: string;
  arrivalDate: string;
  departureDate: string;
  selfTransferUponArrival: boolean;
  selfTransferUponDeparture: boolean;
}

export interface GuestFormErrors {
  [key: string]: string;
}

const validateStage1 = (formValues: GuestFormValues): GuestFormErrors => {
  const errors: GuestFormErrors = {};
  if (!formValues.fullName.trim()) {
    errors.fullName = 'Full name is required.';
  }
  if (!formValues.gender) {
    errors.gender = 'Gender is required.';
  }
  if (!formValues.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!isValidEmail(formValues.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!formValues.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (!isValidPhone(formValues.phone)) {
    errors.phone = 'Enter a valid phone number.';
  }
  return errors;
};

const validateStage2 = (formValues: GuestFormValues): GuestFormErrors => {
  const errors: GuestFormErrors = {};
  if (!formValues.countryOfIssuance) {
    errors.countryOfIssuance = 'Country of issuance is required.';
  }
  if (!formValues.passportNumber) {
    errors.passportNumber = 'Passport number is required.';
  }
  if (!formValues.expiryDate) {
    errors.expiryDate = 'Passport expiry date is required.';
  }
  if (!formValues.emergencyContactName) {
    errors.emergencyContactName = 'Emergency contact name is required.';
  }
  if (!formValues.emergencyContactPhone) {
    errors.emergencyContactPhone = 'Emergency contact phone is required.';
  } else if (!isValidPhone(formValues.emergencyContactPhone)) {
    errors.emergencyContactPhone = 'Enter a valid emergency contact phone.';
  }
  return errors;
};

const validateStage3 = (formValues: GuestFormValues): GuestFormErrors => {
  const errors: GuestFormErrors = {};
  if (!formValues.certificateLevel) {
    errors.certificateLevel = 'Dive certification level is required.';
  }
  if (!formValues.certifyingAgency) {
    errors.certifyingAgency = 'Certifying agency is required.';
  }
  if (!formValues.certificateNumber) {
    errors.certificateNumber = 'Certification number is required.';
  }
  return errors;
};

const validateStage4 = (formValues: GuestFormValues): GuestFormErrors => {
  const errors: GuestFormErrors = {};
  if (!formValues.arrivalDate) {
    errors.arrivalDate = 'Arrival date is required.';
  }
  if (!formValues.departureDate) {
    errors.departureDate = 'Departure date is required.';
  }

  if (formValues.airportTransfer) {
    if (
      !formValues.selfTransferUponArrival &&
      !formValues.arrivalFlight.trim()
    ) {
      errors.arrivalFlight =
        'Arrival flight number is required unless you self-transfer.';
    }
    if (
      !formValues.selfTransferUponDeparture &&
      !formValues.departureFlight.trim()
    ) {
      errors.departureFlight =
        'Departure flight number is required unless you self-transfer.';
    }
  }

  return errors;
};

/**
 * Validates form fields based on the current stage.
 * Stage 5 (Finish) validates stages 1–4 together.
 */
export const validateGuestForm = (
  stage: number,
  formValues: GuestFormValues,
): GuestFormErrors => {
  if (stage === 5 || stage === 0) {
    return {
      ...validateStage1(formValues),
      ...validateStage2(formValues),
      ...validateStage3(formValues),
      ...validateStage4(formValues),
    };
  }

  if (stage === 1) {
    return validateStage1(formValues);
  }
  if (stage === 2) {
    return validateStage2(formValues);
  }
  if (stage === 3) {
    return validateStage3(formValues);
  }
  if (stage === 4) {
    return validateStage4(formValues);
  }

  return {};
};

/** Returns the earliest stage that still has validation errors (1–4), or null. */
export const getFirstInvalidGuestStage = (
  formValues: GuestFormValues,
): number | null => {
  if (Object.keys(validateStage1(formValues)).length > 0) {
    return 1;
  }
  if (Object.keys(validateStage2(formValues)).length > 0) {
    return 2;
  }
  if (Object.keys(validateStage3(formValues)).length > 0) {
    return 3;
  }
  if (Object.keys(validateStage4(formValues)).length > 0) {
    return 4;
  }
  return null;
};
